import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
} from "react";
import { useMutation } from "@tanstack/react-query";
import {
  CancelPaypalSubscriptionInput,
  SubscriptionData,
  SUBSCRIPTION_TIERS,
} from "../types/subscription";
import { API_URL } from "../config";
import { fetchWithAuth, handleAPIResponse } from "../utils/helpers/api";

interface SubscriptionContextType {
  subscription: SubscriptionData | null;
  isDemo: () => boolean;
  hasActiveSubscription: () => boolean;
  isInactive: () => boolean;
  /** Estate libera: unlock read-time, non abbonamento reale. */
  isSummerUnlock: () => boolean;
  isPremium: () => boolean;
  isStandard: () => boolean;
  isLite: () => boolean; // Keep for backward compatibility
  isEssential: () => boolean;
  getSubscriptionVersionString: () => string;
  updateSubscriptionData: (apikey: string) => Promise<void>;
  createBillingSession: () => Promise<string>;
  createCustomerSession: () => Promise<string>;
  loading: boolean;
}

const DEFAULT_SUBSCRIPTION: SubscriptionData = {
  subscriptionStart: new Date().toISOString(),
  periodStart: new Date().toISOString(),
  periodDuration: 1,
  trialDuration: 0,
  cancelInProgress: false,
  isActive: false,
  tokenTrialActive: false,
  tokens: 0,
  channel: "web",
  email: "",
  tier: SUBSCRIPTION_TIERS.premium,
  totalAppointments: 0,
  essentialVersionLimits: {
    pazienti: 0,
    appuntamenti: 0,
  },
};

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(
  undefined,
);

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({
  children,
}) => {
  const [subscription, setSubscription] = useState<SubscriptionData | null>(
    null,
  );
  const [loading, setLoading] = useState(false);

  const updateSubscriptionData = useCallback(async (apikey: string) => {
    if (!apikey) return;

    setLoading(true);
    try {
      // Use browser-native btoa instead of Buffer
      const authHeader = `Basic ${btoa(`placeholder:${apikey}`)}`;

      console.log(
        "Making subscription API call to:",
        `${API_URL}/api/v1/subscription`,
      );

      const response = await fetch(`${API_URL}/api/v1/subscription`, {
        method: "GET",
        headers: {
          Authorization: authHeader,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      console.log("Subscription API response status:", response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      console.log("Subscription API response data:", responseData);

      if (responseData.success) {
        setSubscription(responseData.message);
        console.log("Subscription data updated successfully");
      } else {
        console.error(
          "Failed to fetch subscription data:",
          responseData.errdata,
        );
        setSubscription(DEFAULT_SUBSCRIPTION);
      }
    } catch (error) {
      console.error("Error fetching subscription data:", error);
      setSubscription(DEFAULT_SUBSCRIPTION);
    } finally {
      setLoading(false);
    }
  }, []);

  const createBillingSession = useCallback(async () => {
    const ak = localStorage.getItem("ak");
    if (!ak) throw new Error("API key not found");

    const response = await fetch(`${API_URL}/api/v1/stripe-billing-session`, {
      method: "POST",
      headers: { Authorization: `Basic ${btoa(`placeholder:${ak}`)}` },
    });
    const data = await response.json();
    if (!data.success)
      throw new Error(data.errdata || "Failed to create billing session");
    return data.message;
  }, []);

  const createCustomerSession = useCallback(async () => {
    const ak = localStorage.getItem("ak");
    if (!ak) throw new Error("API key not found");

    const response = await fetch(`${API_URL}/api/v1/stripe-customer-session`, {
      method: "POST",
      headers: { Authorization: `Basic ${btoa(`placeholder:${ak}`)}` },
    });
    const data = await response.json();
    if (!data.success)
      throw new Error(data.errdata || "Failed to create customer session");
    return data.message;
  }, []);

  // Auto-load subscription data on component mount if API key exists
  useEffect(() => {
    const storedApiKey = localStorage.getItem("ak");
    if (storedApiKey && !subscription) {
      updateSubscriptionData(storedApiKey);
    }
  }, [subscription, updateSubscriptionData]);

  const isSummerUnlock = useCallback(() => {
    return subscription?.summerUnlock === true;
  }, [subscription]);

  const isDemo = useCallback(() => {
    return subscription?.tokenTrialActive === true;
  }, [subscription]);

  const hasActiveSubscription = useCallback(() => {
    return subscription?.isActive === true;
  }, [subscription]);

  const isInactive = useCallback(() => {
    if (isDemo()) return false;
    return subscription ? !subscription.isActive : false;
  }, [subscription, isDemo]);

  const isPremium = useCallback(() => {
    return subscription?.tier === SUBSCRIPTION_TIERS.premium;
  }, [subscription]);

  const isStandard = useCallback(() => {
    return subscription?.tier === SUBSCRIPTION_TIERS.lite;
  }, [subscription]);

  const isLite = useCallback(() => {
    return isStandard();
  }, [isStandard]);

  const isEssential = useCallback(() => {
    return subscription?.tier === SUBSCRIPTION_TIERS.essential;
  }, [subscription]);

  const getSubscriptionVersionString = useCallback(() => {
    if (!subscription) return "";

    if (isSummerUnlock()) return "Premium (promo estate)";
    if (isDemo()) return "Demo";

    switch (subscription.tier) {
      case SUBSCRIPTION_TIERS.essential:
        return "Essential";
      case SUBSCRIPTION_TIERS.lite:
        return "Standard";
      case SUBSCRIPTION_TIERS.premium:
        return "Premium";
      default:
        return "";
    }
  }, [subscription, isDemo, isSummerUnlock]);

  const value: SubscriptionContextType = {
    subscription,
    isDemo,
    hasActiveSubscription,
    isInactive,
    isSummerUnlock,
    isPremium,
    isStandard,
    isLite,
    isEssential,
    getSubscriptionVersionString,
    updateSubscriptionData,
    createBillingSession,
    createCustomerSession,
    loading,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error(
      "useSubscription must be used within a SubscriptionProvider",
    );
  }
  return context;
};

/** Stesso flusso app: DELETE /subscription → email Brevo a supporto + cancelInProgress. */
export async function cancelPaypalSubscription(
  input: CancelPaypalSubscriptionInput,
): Promise<unknown> {
  const formData = new FormData();
  formData.append("reason", input.reason);
  formData.append(
    "detail",
    input.reason === "Altro..." ? (input.detail ?? "") : "",
  );
  const response = await fetchWithAuth(`${API_URL}/api/v1/subscription`, {
    method: "DELETE",
    body: formData,
  });
  return handleAPIResponse(response);
}

export const useCancelPaypalSubscription = () =>
  useMutation({
    mutationFn: cancelPaypalSubscription,
  });
